import type { ReactNode } from 'react';
import type { TabItem } from '../organisms';

/**
 * Entity-detail tab CONTRIBUTION REGISTRY.
 *
 * An edition (e.g. AppealStack Law) must ADD tabs to a core detail page without core
 * importing the edition or the edition editing core internals — the FE mirror of the
 * backend boundary (law lives in `src/law/`, inert when off). An edition registers a
 * contribution HOOK per entity type; a core page calls `useContributedTabs(...)` and
 * spreads the result into its own `items`, its `useTabState` valid list, and its panel
 * switch (≈3 lines). Off ⇒ every contribution returns empty ⇒ the page renders
 * byte-for-byte as today.
 *
 * Rules-of-Hooks safety: the registry is populated ONCE at app start — the edition's
 * `tabs.tsx` is imported at the root (main.tsx) before any component renders — so the set
 * of contributions for an entity type is fixed by first render and `useContributedTabs`
 * can call each contribution (itself a hook) in a stable order every render. It never
 * transitions from empty to populated mid-run; a test that does not import the edition's
 * `tabs.tsx` simply sees an empty registry and zero contributions.
 */

export interface ContributedTabs {
  /** Tab chips to append to the page's `Tabs` items (in registration order). */
  items: TabItem[];
  /** Panel node keyed by tab value; rendered when that tab is active. */
  panels: Record<string, ReactNode>;
}

/**
 * A contribution is a React hook — it may call `useFlag` / `useQuery`, so it obeys the
 * Rules of Hooks and is invoked unconditionally from `useContributedTabs`. It reads its
 * own flag and returns empty items+panels when the edition is off.
 */
export type TabContribution<Ctx = unknown> = (ctx: Ctx) => ContributedTabs;

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- heterogeneous ctx per contribution
const REGISTRY = new Map<string, TabContribution<any>[]>();

/**
 * Register a tab contribution for an entity type (e.g. 'clients', 'properties'). Call at
 * module load (an edition's `tabs.tsx`), never inside a component.
 */
export function registerEntityTabContribution<Ctx = unknown>(
  entityType: string,
  contribution: TabContribution<Ctx>,
): void {
  const list = REGISTRY.get(entityType) ?? [];
  list.push(contribution as TabContribution<unknown>);
  REGISTRY.set(entityType, list);
}

/**
 * Merge every registered contribution for an entity type into one set of tab items,
 * panels, and the list of contributed tab values (for the page's `useTabState` valid
 * array). All-empty when nothing is registered or every contribution's flag is off.
 */
export function useContributedTabs<Ctx = unknown>(
  entityType: string,
  ctx: Ctx,
): { tabItems: TabItem[]; panels: Record<string, ReactNode>; values: string[] } {
  const contributions = REGISTRY.get(entityType) ?? [];
  const tabItems: TabItem[] = [];
  const panels: Record<string, ReactNode> = {};
  for (const contribution of contributions) {
    // Stable order — the registry is fixed before first render (see file header), so this
    // loop calls the same hooks in the same order every render.
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const contributed = contribution(ctx);
    tabItems.push(...contributed.items);
    Object.assign(panels, contributed.panels);
  }
  return { tabItems, panels, values: tabItems.map((t) => t.value) };
}

/** Test-only: clear the registry between tests that register contributions. */
export function __resetEntityTabsForTest(): void {
  REGISTRY.clear();
}
